import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardTitle,
    CardContent,
} from "../components/ui/card";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Label,
} from "recharts";
import { User, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import axios from "../axios";
import { useWidth } from "../hooks/useWidth";

// Funkcija za formatiranje števila v valuto
function formatCurrency(value) {
    return new Intl.NumberFormat("sl-SI", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    }).format(value);
}

// Funkcija za pretvorbo izbranega obdobja v število dni
function getDaysForTimeRange(tr) {
    switch (tr) {
        case "7d":
            return 7;
        case "30d":
            return 30;
        case "90d":
            return 90;
        case "ytd":
            return 365;
        case "1y":
            return 365;
        default:
            return 30;
    }
}

// Seznam agentov v določenem vrstnem redu
const agentsIds = [
    "AP", // Alja Pirkmajer
    "EČ", // Esad Čeman
    "SĐ", // Sanja Đukič
    "ND", // Nenad Dončič
    "NMR", // Nataša Mulej
    "RL", // Rok Lujanac
    "JC", // Jana Caserman
    "MJ", // Metka Junc
    "UT", // Urška Tomšič
    "JD", // Janez Dragar
    "TD", // Tea Drozg
    "KK", // Ksenija Kus
    "ZP", // Zdenko Peršolja
    "RLM", // Roman Lesjak
    "JP", // Juš
    "STO", // Stoja
];

// Seznam agentov z imeni
const agentMap = {
    AP: {
        ime: "Alja Pirkmajer",
        promet2023: 178057.5,
        stTransakcij: 161,
        povprecnaVrednost: 1105.95,
        prometLastDecember: 178057.5,
    },
    EČ: {
        ime: "Esad Čeman",
        promet2023: 347587.97,
        stTransakcij: 122,
        povprecnaVrednost: 2849.08,
        prometLastDecember: 347587.97,
    },
    SĐ: {
        ime: "Sanja Đukič",
        promet2023: 154334.22,
        stTransakcij: 97,
        povprecnaVrednost: 1591.07,
        prometLastDecember: 154334.22,
    },
    ND: {
        ime: "Nenad Dončič",
        promet2023: 258808.05,
        stTransakcij: 109,
        povprecnaVrednost: 2374.39,
        prometLastDecember: 258808.05,
    },
    NMR: {
        ime: "Nataša Mulej",
        promet2023: 197105.49,
        stTransakcij: 120,
        povprecnaVrednost: 1642.55,
        prometLastDecember: 197105.49,
    },
    RL: {
        ime: "Rok Lujanac",
        promet2023: 321727.78,
        stTransakcij: 128,
        povprecnaVrednost: 2513.5,
        prometLastDecember: 321727.78,
    },
    JC: {
        ime: "Jana Caserman",
        promet2023: 151050.25,
        stTransakcij: 99,
        povprecnaVrednost: 1525.76,
        prometLastDecember: 151050.25,
    },
    MJ: {
        ime: "Metka Junc",
        promet2023: 33821.56,
        stTransakcij: 12,
        povprecnaVrednost: 2818.46,
        prometLastDecember: 33821.56,
    },
    UT: {
        ime: "Urška Tomšič",
        promet2023: 189782.3,
        stTransakcij: 75,
        povprecnaVrednost: 2530.43,
        prometLastDecember: 189782.3,
    },
    JD: {
        ime: "Janez Dragar",
        promet2023: 57268.28,
        stTransakcij: 36,
        povprecnaVrednost: 1590.79,
        prometLastDecember: 57268.28,
    },
    TD: {
        ime: "Tea Drozg",
        promet2023: 12128.26,
        stTransakcij: 4,
        povprecnaVrednost: 3032.07,
        prometLastDecember: 12128.26,
    },
    KK: {
        ime: "Ksenija Kus",
        promet2023: 0,
        stTransakcij: 0,
        povprecnaVrednost: 0,
        prometLastDecember: 0,
    },
    ZP: {
        ime: "Zdenko Peršolja",
        promet2023: 5220.0,
        stTransakcij: 4,
        povprecnaVrednost: 1305.0,
        prometLastDecember: 5220.0,
    },
    RLM: {
        ime: "Roman Lesjak",
        promet2023: 16103.0,
        stTransakcij: 6,
        povprecnaVrednost: 2683.83,
        prometLastDecember: 16103.0,
    },
    JP: {
        ime: "Juš",
        promet2023: 16103.0,
        stTransakcij: 6,
        povprecnaVrednost: 2683.83,
        prometLastDecember: 16103.0,
    },
    STO: {
        ime: "Stoja",
        promet2023: 0,
        stTransakcij: 0,
        povprecnaVrednost: 0,
        prometLastDecember: 0,
    },
};

const AgentDashboard = () => {
    const { isLower } = useWidth(640);
    const [selectedAgentId, setSelectedAgentId] = useState("EČ");
    const [prometData, setPrometData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [sum, setSum] = useState(0);
    const [avg, setAvg] = useState(0);
    const [ytdSum, setYtdSum] = useState(0);
    const [decPrediction, setDecPrediction] = useState(0);
    const [percentChange, setPercentChange] = useState(0);
    const [agentProfile, setAgentProfile] = useState({
        currentYearRevenue: 0,
        invoiceCount: 0,
        monthlyAverage: 0,
    });

    const [odsotnostData, setOdsotnostData] = useState({
        bolniska: 0,
        vikendiPrazniki: 40,
        delovniDnevi: 20,
        skupaj: 40,
    });

    const actualAgentId = selectedAgentId === "NR" ? "NMR" : selectedAgentId;
    const selectedAgent = agentMap[actualAgentId] || {};

    // Fetch available years on component mount
    useEffect(() => {
        const fetchYears = async () => {
            try {
                const response = await axios.get("/api/agent/years");
                if (response.data.success) {
                    setAvailableYears(response.data.data);
                    // Set the most recent year as default if available
                    if (response.data.data.length > 0) {
                        setYear(response.data.data[0]);
                    }
                }
            } catch (err) {
                console.error("Error fetching years:", err);
            }
        };

        fetchYears();
    }, []);

    useEffect(() => {
        const fetchAgentData = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await axios.get("/api/agent/stats", {
                    params: {
                        agentName: selectedAgent.ime,
                        year,
                    },
                });

                if (response.data.success) {
                    const { data } = response.data;
                    const agentStats = data.agent.stats;

                    // Create forecast data as a straight target line
                    const forecastData = Array.from(
                        { length: 12 },
                        (_, index) => ({
                            month: data.agent.monthlyData[index].month,
                            // Show the December target value for all months
                            forecast: agentStats.currentYearForecast,
                        })
                    );

                    // Calculate cumulative revenue and transaction stats
                    let runningTotal = 0;
                    let transactionCount = 0;
                    const revenueData = data.agent.monthlyData
                        .map((item, index) => {
                            if (item.revenue > 0) {
                                runningTotal += item.revenue;
                                transactionCount += 1; // Count months with revenue
                                return {
                                    ...item,
                                    cumulativeRevenue: runningTotal,
                                };
                            }
                            return null;
                        })
                        .filter((item) => item !== null);

                    // Calculate average transaction value
                    const averageValue =
                        transactionCount > 0
                            ? runningTotal / transactionCount
                            : 0;

                    setPrometData({
                        monthlyData: revenueData,
                        forecastData,
                        lastYearRevenue: agentStats.lastYearRevenue,
                        actualInvoices: agentStats.actualInvoices,
                        preInvoices: agentStats.preInvoices,
                    });

                    // Set other stats
                    setSum(agentStats.cumulativeRevenue);
                    setAvg(agentStats.cumulativeRevenue / 12);
                    setYtdSum(agentStats.cumulativeRevenue);
                    setDecPrediction(agentStats.currentYearForecast);

                    // Handle percentage change
                    const rawChange = agentStats.yearOverYearChange;

                    // Store the raw number, it already has the correct sign
                    setPercentChange(rawChange);

                    // Calculate current year revenue and stats
                    let totalRevenue = 0;
                    let monthsWithRevenue = 0;
                    data.agent.monthlyData.forEach((month) => {
                        if (month.revenue > 0) {
                            totalRevenue += month.revenue;
                            monthsWithRevenue++;
                        }
                    });

                    // Calculate monthly average (total divided by months with revenue)
                    const monthlyAverage =
                        monthsWithRevenue > 0
                            ? totalRevenue / monthsWithRevenue
                            : 0;

                    // Use only actual invoices, ignore pre-invoices
                    const invoiceCount = agentStats.actualInvoices || 0;

                    // Update agent profile
                    setAgentProfile({
                        currentYearRevenue: totalRevenue,
                        invoiceCount: invoiceCount,
                        monthlyAverage: monthlyAverage,
                    });
                } else {
                    console.error("API returned success: false");
                    setError("Napaka pri pridobivanju prometa.");
                }
            } catch (err) {
                console.error("Error fetching agent data:", err);
                console.error("Error response:", err.response?.data);
                setError(
                    err.response?.data?.message ||
                        "Napaka pri pridobivanju prometa."
                );
            } finally {
                setLoading(false);
            }
        };

        if (selectedAgent.ime) {
            fetchAgentData();
        } else {
            console.log("No agent selected");
        }
    }, [selectedAgent.ime, year]);

    if (loading) {
        return (
            <div className="flex items-center justify-center w-full min-h-screen p-4 mx-auto max-w-7xl bg-gray-50">
                <div className="text-gray-500">Nalaganje podatkov...</div>
            </div>
        );
    }

    if (error || !prometData) {
        return (
            <div className="flex items-center justify-center w-full min-h-screen p-4 mx-auto max-w-7xl bg-gray-50">
                <div className="text-red-500">
                    {error || "Podatki niso na voljo."}
                </div>
            </div>
        );
    }

    const fadeInUp = {
        hidden: { opacity: 0, y: 10 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.1, type: "spring", stiffness: 60 },
        }),
    };

    const timeRanges = [
        { value: "7d", label: "1 Teden" },
        { value: "30d", label: "30 Dni" },
        { value: "90d", label: "90 Dni" },
        { value: "ytd", label: "Leto do danes" },
        { value: "1y", label: "1 Leto" },
    ];

    return (
        <div className="container px-4 py-8 mx-auto">
            {/* Headline */}
            <motion.div
                className="mb-8 text-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl lg:text-4xl">
                    Statistika{" "}
                    <span
                        className="font-extrabold"
                        style={{ color: "#ff1453" }}
                    >
                        STOJINIH
                    </span>{" "}
                    agentov
                </h1>
            </motion.div>

            {/* Controls Section */}
            <div className="flex flex-col items-start justify-between gap-4 mb-6 overflow-hidden">
                {/* Agent Selector - Scrollable on mobile */}
                <div className="w-full overflow-x-auto min-h-12">
                    <motion.div
                        className="flex gap-2 pb-2 md:pb-0"
                        initial="hidden"
                        animate="visible"
                        variants={fadeInUp}
                        style={{
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "none",
                            msOverflowStyle: "none",
                        }}
                    >
                        {agentsIds.map((agent, i) => (
                            <motion.button
                                key={agent}
                                onClick={() => setSelectedAgentId(agent)}
                                className={`px-4 py-2 rounded-lg transition-all duration-200 text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                                    selectedAgentId === agent ||
                                    (agent === "NMR" &&
                                        selectedAgentId === "NR")
                                        ? "bg-blue-600 text-white shadow-md scale-105"
                                        : "bg-white hover:bg-gray-50 border border-gray-200 text-gray-800"
                                }`}
                                variants={fadeInUp}
                                custom={i}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {agent}
                            </motion.button>
                        ))}
                    </motion.div>
                </div>

                {/* Year Selector */}
                <div className="flex items-center w-full gap-2 md:w-auto">
                    <select
                        id="year"
                        value={year}
                        onChange={(e) => setYear(parseInt(e.target.value))}
                        className="w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm md:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        {availableYears.map((y) => (
                            <option key={y} value={y}>
                                {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 md:gap-6">
                {/* Agent Profile Card */}
                <motion.div
                    className="lg:col-span-1"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Card className="transition-all duration-300 border border-gray-200 hover:shadow-lg">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                                <User size={20} className="text-blue-500" />
                                Profil Agenta
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col items-center">
                                <motion.div
                                    className="relative flex items-center justify-center w-20 h-20 mb-4 rounded-full sm:w-24 sm:h-24 bg-blue-50"
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 200,
                                        damping: 20,
                                    }}
                                >
                                    <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse" />
                                    <User
                                        size={32}
                                        className="z-10 text-blue-500"
                                    />
                                </motion.div>
                                <h2 className="mb-4 text-lg font-bold text-center sm:text-xl">
                                    {selectedAgent.ime}
                                </h2>
                                <div className="w-full space-y-3">
                                    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                                        <span className="mr-2 text-xs text-gray-600 sm:text-sm">
                                            Promet ({year})
                                        </span>
                                        <span className="text-sm font-semibold sm:text-base">
                                            {formatCurrency(
                                                agentProfile.currentYearRevenue
                                            )}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                                        <span className="mr-2 text-xs text-gray-600 sm:text-sm">
                                            Število računov
                                        </span>
                                        <span className="text-sm font-semibold sm:text-base">
                                            {agentProfile.invoiceCount}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                                        <span className="mr-2 text-xs text-gray-600 sm:text-sm">
                                            Povprečno (mesec)
                                        </span>
                                        <span className="text-sm font-semibold sm:text-base">
                                            {formatCurrency(
                                                agentProfile.monthlyAverage
                                            )}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Revenue Chart Card */}
                <motion.div
                    className="lg:col-span-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <Card className="transition-all duration-300 border border-gray-200 hover:shadow-lg">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                                <TrendingUp
                                    size={20}
                                    className="text-blue-500"
                                />
                                Letni Promet ({year})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[250px] sm:h-[300px] md:h-[400px] sm:mx-0">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                        margin={{
                                            top: 20,
                                            right: 10,
                                            left: 0,
                                            bottom: 5,
                                        }}
                                    >
                                        <XAxis
                                            dataKey="month"
                                            allowDuplicatedCategory={false}
                                            tick={{
                                                fontSize: 10,
                                                fill: "#4B5563",
                                            }}
                                            tickMargin={10}
                                            height={50}
                                            interval="preserveStartEnd"
                                            minTickGap={20}
                                            tickFormatter={(value) => {
                                                return isLower ? "" : value;
                                            }}
                                        />
                                        <YAxis
                                            tickFormatter={(value) => {
                                                return isLower
                                                    ? ""
                                                    : `${value} €`;
                                            }}
                                            tick={{
                                                fontSize: 10,
                                                fill: "#4B5563",
                                            }}
                                            width={isLower ? 5 : 60}
                                        />
                                        <Tooltip
                                            formatter={(value, name) => {
                                                const label =
                                                    name === "cumulativeRevenue"
                                                        ? "Skupni promet"
                                                        : "Napoved";
                                                return [
                                                    formatCurrency(value),
                                                    label,
                                                ];
                                            }}
                                            contentStyle={{
                                                backgroundColor:
                                                    "rgba(255, 255, 255, 0.98)",
                                                border: "none",
                                                borderRadius: "0.5rem",
                                                boxShadow:
                                                    "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                                padding: "0.75rem",
                                                fontSize: "0.875rem",
                                            }}
                                            cursor={{ strokeWidth: 2 }}
                                            wrapperStyle={{
                                                outline: "none",
                                                touchAction: "pan-x pan-y",
                                            }}
                                        />
                                        <Line
                                            data={prometData?.monthlyData || []}
                                            type="monotone"
                                            name="cumulativeRevenue"
                                            dataKey="cumulativeRevenue"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            dot={{
                                                stroke: "#3b82f6",
                                                strokeWidth: 2,
                                                r: isLower ? 3 : 4,
                                                fill: "#fff",
                                            }}
                                            activeDot={{
                                                r: isLower ? 5 : 6,
                                                stroke: "#3b82f6",
                                                strokeWidth: 2,
                                                fill: "#fff",
                                            }}
                                        />
                                        <Line
                                            data={
                                                prometData?.forecastData || []
                                            }
                                            type="monotone"
                                            name="forecast"
                                            dataKey="forecast"
                                            stroke="#14b8a6"
                                            strokeWidth={2}
                                            strokeDasharray="5 5"
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                {/* Forecast Card */}
                <motion.div
                    className="lg:col-span-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <Card className="transition-all duration-300 border border-gray-200 hover:shadow-lg">
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                                <TrendingUp
                                    size={20}
                                    className="text-[#d31245]"
                                />
                                Napoved in Primerjava
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 md:gap-4">
                                <div className="p-3 rounded-lg md:p-4 bg-gray-50">
                                    <div className="mb-1 text-xs text-gray-600 sm:text-sm">
                                        Letošnji promet
                                    </div>
                                    <div className="text-lg font-bold text-blue-600 sm:text-xl md:text-2xl">
                                        {formatCurrency(ytdSum)}
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg md:p-4 bg-gray-50">
                                    <div className="mb-1 text-xs text-gray-600 sm:text-sm">
                                        Napoved za december
                                    </div>
                                    <div className="text-lg font-bold text-green-600 sm:text-xl md:text-2xl">
                                        {formatCurrency(decPrediction)}
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg md:p-4 bg-gray-50 sm:col-span-2 md:col-span-1">
                                    <div className="mb-1 text-xs text-gray-600 sm:text-sm">
                                        Sprememba glede na lani
                                    </div>
                                    <div
                                        className={`text-lg sm:text-xl md:text-2xl font-bold ${
                                            percentChange >= 0
                                                ? "text-green-600"
                                                : "text-red-600"
                                        }`}
                                    >
                                        {percentChange >= 0 ? "+" : ""}
                                        {percentChange.toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75">
                    <div className="flex flex-col items-center">
                        <div className="w-16 h-16 border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin" />
                        <p className="mt-4 text-gray-600">
                            Nalaganje podatkov...
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgentDashboard;
